// Settings — the registry's values at one scope, and changing them (F-ORG-02; spec/03 §9, spec/13 §13,
// spec/14 §14).
//
//   GET /v1/hq/settings                                    platform defaults and locks      hq:settings
//   PUT /v1/hq/settings                                    change them, If-Match            hq:settings
//   GET /v1/hq/organizations/{o}/settings                  one organisation's, as HQ        hq:settings
//   PUT /v1/hq/organizations/{o}/settings                  set, lock or unlock there        hq:settings
//   GET /v1/organizations/{o}/settings                     the organisation's screen        settings:read
//   PUT /v1/organizations/{o}/settings                     change them, If-Match            settings:read + each key's editPermission
//   GET /v1/organizations/{o}/venues/{v}/settings          the venue's screen               settings:read
//   PUT /v1/organizations/{o}/venues/{v}/settings          change them, a class's, a zone's settings:read + each key's editPermission
//
// **One resource per scope, generated from the registry.** A GET returns every setting the screen at
// that scope shows — its value, where the value comes from, what "reset to inherited" would give, its
// description, the JSON Schema its control is built from, its group, whether it is locked and whether
// the caller may change it — so a console renders a settings screen with no code that knows any
// setting by name (spec/13 §13). A setting added to the registry appears on the next GET.
//
// **Where the routes are.** Organisation and venue under the organisation, like every organisation-
// scoped route (spec/08 §1.3, and F-ORG-01's venues). The platform's under `/v1/hq`, where every
// route is behind one middleware that insists on a platform role and a second factor (spec/08
// §4.16): the issue named `/v1/settings`, and spec/08 §4.16 lists "platform settings" among HQ's
// routes — the second is the one the middleware already enforces. HQ reaches one organisation's
// settings — to lock one there (spec/03 §9) — at `/v1/hq/organizations/{o}/settings`, through the
// audited door, as it sets a venue's discovery flags (F-ORG-01).
//
// **A change is a list of changes**, applied together or not at all, each one a value (`value`) or a
// return to the inherited value (`reset: true`), at the screen's own scope — or, on the venue's
// screen, at one of its station classes or zones, for a setting that accepts that scope. HQ's
// changes may also lock or unlock (`locked`). Each change that changes something is its own action:
// one audit row, with the key, the scope, the value and the lock before and after (spec/07 §11.1),
// and one `org.setting.changed` event.
//
// **Concurrency** (spec/08 §2.5). Every GET carries an `ETag` naming the stored values the screen was
// built from — this scope's, and every scope above it — and a PUT must send it as `If-Match`: a
// missing one is 400 and a stale one 412, with the current ETag, so an editor never overwrites a
// change they have not seen, including HQ locking the setting under them.
//
// **Refusals**, in the order they are checked: the body (400, with the field); a setting outside the
// caller's plan (402 `entitlement-required`, spec/14 §4.2); the key's `editPermission`, or a
// setting this screen does not edit (403 `forbidden`); a stale ETag (412); and a lock at or above
// the scope, for anyone but HQ (403 `setting-locked`, naming the scope that holds it).
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { OrganizationIdSchema, StationClassIdSchema, VenueIdSchema, ZoneIdSchema } from "../ids/schema.js";
import { CurrencyCodeSchema, IsoDateTimeSchema, PermissionSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema, VenueScopeParamsSchema } from "../primitives/scope.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, problemResponse } from "../openapi/responses.js";
import { HQ_SETTINGS, SETTINGS_READ } from "../permissions/index.js";
import { SETTING_AUDIENCES, SETTING_KEY_PATTERN, SETTING_LOCK_SCOPES, SETTING_SCOPES, } from "../settings/define-setting.js";
import { ENTITLEMENT_KEYS } from "../settings/entitlements.js";
import { SETTING_GROUP_IDS } from "../settings/groups.js";
import { SETTING_UNITS } from "../settings/units.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { ConditionalWriteHeaderSchema } from "./venues.js";
/**
 * The OpenAPI extension a settings write declares beside its `x-clavian-permission`: **and** every
 * change needs its key's `editPermission` (spec/03 §9). The route's own permission is the floor —
 * reading the screen — and this names the per-key check the handler adds on top of it, so the
 * document says both, and the build-time check that every permission gates an endpoint counts each
 * registered setting's `editPermission` as gated by these routes.
 */
export const SETTING_PERMISSION_EXTENSION = 'x-clavian-setting-permission';
export const SETTING_PERMISSION_PER_KEY = 'editPermission';
/** The most changes one PUT carries. A screen saves a group at a time; this is far above any group. */
export const MAX_SETTING_CHANGES = 50;
export const SettingKeySchema = z
    .string()
    .max(120)
    .regex(SETTING_KEY_PATTERN, 'Expected a setting key, namespace.settingName')
    .openapi('SettingKey', { description: 'A key in the settings registry.', example: 'sessions.offlinePolicy' });
export const SettingScopeSchema = z.enum(SETTING_SCOPES).openapi('SettingScope', {
    description: 'Where a value may be set. Resolution runs station → station class → zone → venue → organisation → platform.',
});
export const SettingLockScopeSchema = z.enum(SETTING_LOCK_SCOPES).openapi('SettingLockScope', {
    description: 'Where HQ may lock a setting so that no scope below can override it.',
});
/** Where a screen's value comes from (spec/13 §13): the registry's default, or a scope's stored value. */
export const SETTING_SOURCES = ['default', 'platform', 'organization', 'venue'];
export const SettingSourceSchema = z.enum(SETTING_SOURCES).openapi('SettingSource', {
    description: 'Where a value comes from: the registry default, or the value stored at a scope.',
});
export const SettingGroupSchema = z.enum(SETTING_GROUP_IDS).openapi('SettingGroup', {
    description: 'The heading a settings screen shows a setting under (spec/13 §13).',
});
export const SettingUnitSchema = z.enum(SETTING_UNITS).openapi('SettingUnit', {
    description: 'How a settings screen shows and takes a value — display only. `money`: the value is integer minor units of the view’s `scope.currency`, shown and typed in the major unit.',
});
export const SettingAudienceSchema = z.enum(SETTING_AUDIENCES).openapi('SettingAudience', {
    description: 'The lowest settings screen that shows the setting: HQ’s, the organisation’s or the venue’s.',
});
export const EntitlementKeySchema = z.enum(ENTITLEMENT_KEYS).openapi('EntitlementKey', {
    description: 'A subscription entitlement (spec/14 §4.1).',
});
/** The screens a settings resource belongs to. */
export const SETTINGS_TARGETS = ['platform', 'organization', 'venue'];
export const InheritedSettingValueSchema = z
    .object({
    /** What the setting would be at this scope with this scope's own value removed. */
    value: z.unknown(),
    source: SettingSourceSchema,
})
    .openapi('InheritedSettingValue', { description: 'What "reset to inherited" would give, and where it comes from.' });
export const PlaceSettingValueSchema = z
    .object({
    scopeType: z.enum(['stationClass', 'zone']),
    /** Set when `scopeType` is `stationClass`. */
    stationClassId: StationClassIdSchema.nullable(),
    /** Set when `scopeType` is `zone`. */
    zoneId: ZoneIdSchema.nullable(),
    value: z.unknown(),
    updatedAt: IsoDateTimeSchema,
})
    .openapi('PlaceSettingValue', {
    description: 'A value stored for one station class or zone of the venue. It applies to the stations there — unless a lock above the venue binds them.',
});
/**
 * A value one venue keeps of its own, shown on the organisation's screen (2026-10-07, spec/13 §13): "3 venues use
 * their own value". Read-only and additive: it is not part of the screen's ETag, which versions what an organisation
 * write is based on, and an organisation write never touches a venue's value.
 */
export const VenueSettingValueSchema = z
    .object({
    venueId: VenueIdSchema,
    value: z.unknown(),
    updatedAt: IsoDateTimeSchema,
})
    .openapi('VenueSettingValue', {
    description: 'A value stored for one live venue of the organisation. It applies there instead of the organisation’s.',
});
export const SettingEntrySchema = z
    .object({
    key: SettingKeySchema,
    group: SettingGroupSchema,
    description: z.string(),
    /** The value's JSON Schema, from the registry: what the screen builds the control from. */
    schema: z.record(z.string(), z.unknown()),
    default: z.unknown(),
    scopes: z.array(SettingScopeSchema),
    visibleTo: SettingAudienceSchema,
    editPermission: PermissionSchema,
    /**
     * The subscription entitlement it needs; absent when every plan has it. (Absent rather than
     * null: the generators give an enum no null member.)
     */
    entitlement: EntitlementKeySchema.optional(),
    /** The value in force at this scope. */
    value: z.unknown(),
    source: SettingSourceSchema,
    inherited: InheritedSettingValueSchema,
    /** This scope stores a value of its own — the one "reset to inherited" removes. */
    hasOverride: z.boolean(),
    /**
     * The scope whose lock binds this one; absent when nothing is locked. A locked value is the value,
     * whatever is stored below it.
     */
    lockedAt: SettingLockScopeSchema.optional(),
    /** Where HQ may lock it, as the specification marks it; empty for a setting that is never locked. */
    lockableAt: z.array(SettingLockScopeSchema),
    /**
     * A dated setting (spec/03 §9): its value takes effect from a business day and is changed through
     * its owning module's own endpoint, never here. It is read-only on this screen (`editable` is
     * false), and the value shown is the registry default until the module resolves a dated one.
     */
    dated: z.boolean(),
    /**
     * How the screen shows and takes the value; absent for a value shown as it is. `money`: the value
     * (and the default, the inherited value and every override) stays integer minor units on the wire;
     * the screen shows and accepts the major unit of `scope.currency`. A money field inside a structured
     * value carries `x-clavian-unit: money` in `schema` instead.
     */
    unit: SettingUnitSchema.optional(),
    /** The caller may change the value at this screen's own scope. */
    editable: z.boolean(),
    /** Every scope the caller may change it at from this screen: its own, and a venue's classes and zones. */
    editableScopes: z.array(SettingScopeSchema),
    /** On a venue's screen, the values stored for its station classes and zones. Empty elsewhere. */
    overrides: z.array(PlaceSettingValueSchema),
    /**
     * On an organisation's screen, the values its live venues store of their own, for a setting that takes venue
     * scope — present for HQ and for a member whose staff access covers every venue, absent otherwise (a member
     * scoped to some venues never reads another venue's values here). Absent on every other screen.
     */
    venueOverrides: z.array(VenueSettingValueSchema).optional(),
})
    .openapi('SettingEntry', { description: 'One setting as a settings screen shows it (spec/13 §13).' });
export const SettingGroupHeadingSchema = z
    .object({ id: SettingGroupSchema, title: z.string() })
    .openapi('SettingGroupHeading', { description: 'A heading on a settings screen, in the order to show them.' });
export const SettingsViewScopeSchema = z
    .object({
    type: z.enum(SETTINGS_TARGETS),
    organizationId: OrganizationIdSchema.nullable(),
    venueId: VenueIdSchema.nullable(),
    /**
     * The currency this screen's money settings are in: the organisation's (a venue trades in its
     * organisation's, F-ORG-01 rule 5); on HQ's platform screen, the platform default (INR).
     */
    currency: CurrencyCodeSchema,
})
    .openapi('SettingsViewScope', { description: 'The scope a settings screen edits.' });
export const SettingsViewSchema = z
    .object({
    scope: SettingsViewScopeSchema,
    /** The groups that have at least one setting here, in screen order. */
    groups: z.array(SettingGroupHeadingSchema),
    /** Sorted by key. */
    settings: z.array(SettingEntrySchema),
})
    .openapi('SettingsView', { description: 'A generated settings screen: every setting it shows, and each one’s state.' });
// ---------------------------------------------------------------------------------------------
// Changes
// ---------------------------------------------------------------------------------------------
const changeShape = {
    key: SettingKeySchema,
    /** The new value, as the setting's own schema accepts it. Omitted with `reset`. */
    value: z.unknown().optional(),
    /** `true`: remove this scope's value and inherit the one above. Not with `value`. */
    reset: z.boolean().optional(),
};
/** Exactly one of a value and a reset — a change that says neither, or both, is not a change. */
function oneOfValueOrReset(change, context) {
    const hasValue = change.value !== undefined;
    const resets = change.reset === true;
    if (hasValue === resets) {
        context.addIssue({
            code: 'custom',
            message: resets ? 'Send a value or reset: true, not both.' : 'Send a value, or reset: true to inherit.',
            path: resets ? ['reset'] : ['value'],
        });
    }
}
export const SettingChangeSchema = z
    .object(changeShape)
    .strict()
    .superRefine(oneOfValueOrReset)
    .openapi('SettingChange', { description: 'Set a value at this scope, or reset it to the inherited one.' });
export const VenueSettingChangeSchema = z
    .object({
    ...changeShape,
    /** Set it for one station class of this venue instead of the venue, when the setting accepts that scope. */
    stationClassId: StationClassIdSchema.optional(),
    /** Set it for one zone of this venue instead of the venue, when the setting accepts that scope. */
    zoneId: ZoneIdSchema.optional(),
})
    .strict()
    .superRefine((change, context) => {
    oneOfValueOrReset(change, context);
    if (change.stationClassId !== undefined && change.zoneId !== undefined) {
        context.addIssue({ code: 'custom', message: 'A change is for a station class or a zone, not both.', path: ['zoneId'] });
    }
})
    .openapi('VenueSettingChange', {
    description: 'Set or reset a value for the venue, or for one of its station classes or zones.',
});
export const LockableSettingChangeSchema = z
    .object({
    ...changeShape,
    /**
     * HQ only: lock (true) or unlock (false) the value set here, so no scope below can override it. With
     * `value`, and only where the setting is lockable (`lockableAt`).
     */
    locked: z.boolean().optional(),
})
    .strict()
    .superRefine((change, context) => {
    oneOfValueOrReset(change, context);
    if (change.locked !== undefined && change.reset === true) {
        context.addIssue({
            code: 'custom',
            message: 'A reset removes the value and its lock; send locked only with a value.',
            path: ['locked'],
        });
    }
})
    .openapi('LockableSettingChange', { description: 'Set, lock, unlock or reset a value (HQ).' });
const changesOf = (change) => z.array(change).min(1, 'Send at least one change.').max(MAX_SETTING_CHANGES);
export const PutSettingsSchema = z.object({ changes: changesOf(SettingChangeSchema) }).strict().openapi('PutSettings');
export const PutVenueSettingsSchema = z
    .object({ changes: changesOf(VenueSettingChangeSchema) })
    .strict()
    .openapi('PutVenueSettings');
export const PutLockableSettingsSchema = z
    .object({ changes: changesOf(LockableSettingChangeSchema) })
    .strict()
    .openapi('PutLockableSettings');
// ---------------------------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------------------------
const settingsResponse = (description) => ({
    description,
    headers: z.object({ ETag: z.string().openapi({ description: 'The version to send back as If-Match.' }) }),
    content: { [JSON_CONTENT_TYPE]: { schema: SettingsViewSchema } },
});
const forbidden = problemResponse('Your role does not allow this, the setting is not one this screen changes, or it is locked above this scope.', 'forbidden', 'second-factor-required', 'setting-locked');
const entitlementRequired = problemResponse('A setting you changed is not in the organisation’s plan.', 'entitlement-required');
const stale = problemResponse('Someone changed these settings since you read them.', 'stale-write');
const invalid = problemResponse('The change is not one the registry accepts: an unknown key, a scope it does not take, or a value its schema refuses.', 'validation-failed');
export const getPlatformSettingsRoute = createRoute({
    method: 'get',
    path: '/v1/hq/settings',
    operationId: 'getPlatformSettings',
    tags: ['settings'],
    summary: 'Read the platform defaults and locks',
    description: 'Every setting in the registry, with its platform value and whether it is locked there (spec/14 §14). Requires `hq:settings`.',
    [PERMISSION_EXTENSION]: HQ_SETTINGS,
    responses: {
        200: settingsResponse('The platform settings screen.'),
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const putPlatformSettingsRoute = createRoute({
    method: 'put',
    path: '/v1/hq/settings',
    operationId: 'putPlatformSettings',
    tags: ['settings'],
    summary: 'Change platform defaults and locks',
    description: 'Sets, locks, unlocks or resets platform values, all or none. A lock at platform scope binds every organisation and venue, and is refused (400) on a setting not lockable there (`lockableAt`). ' +
        'Send the `ETag` you read as `If-Match`. Requires `hq:settings`. Each change is audited.',
    [PERMISSION_EXTENSION]: HQ_SETTINGS,
    request: {
        headers: ConditionalWriteHeaderSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: PutLockableSettingsSchema } }, required: true },
    },
    responses: {
        200: settingsResponse('The platform settings screen as it is now.'),
        400: invalid,
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        412: stale,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getHqOrganizationSettingsRoute = createRoute({
    method: 'get',
    path: '/v1/hq/organizations/{organizationId}/settings',
    operationId: 'getHqOrganizationSettings',
    tags: ['settings'],
    summary: 'Read one organisation’s settings, as HQ',
    description: 'Every setting that can be set or locked for the organisation, with its value and lock. The read is audited in the organisation’s log. Requires `hq:settings`.',
    [PERMISSION_EXTENSION]: HQ_SETTINGS,
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: settingsResponse('The organisation’s settings, as HQ sees them.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const putHqOrganizationSettingsRoute = createRoute({
    method: 'put',
    path: '/v1/hq/organizations/{organizationId}/settings',
    operationId: 'putHqOrganizationSettings',
    tags: ['settings'],
    summary: 'Set, lock or unlock an organisation’s settings, as HQ',
    description: 'A lock here binds the organisation’s venues, station classes and zones: their editors are refused with `setting-locked`. A lock is refused (400) on a setting not lockable there (`lockableAt`). ' +
        'Refused when the setting is locked at platform scope. Send the `ETag` you read as `If-Match`. Requires `hq:settings`. ' +
        'Each change is one audit row in the organisation’s log.',
    [PERMISSION_EXTENSION]: HQ_SETTINGS,
    request: {
        params: OrganizationScopeParamsSchema,
        headers: ConditionalWriteHeaderSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: PutLockableSettingsSchema } }, required: true },
    },
    responses: {
        200: settingsResponse('The organisation’s settings as they are now.'),
        400: invalid,
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such organisation.', 'not-found'),
        412: stale,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getOrganizationSettingsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/settings',
    operationId: 'getOrganizationSettings',
    tags: ['settings'],
    summary: 'Read the organisation’s settings screen',
    description: 'Every setting the organisation’s screen shows, with its value, where it comes from, what it would inherit, whether it is locked and whether you may change it. Requires `settings:read`.',
    [PERMISSION_EXTENSION]: SETTINGS_READ,
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: settingsResponse('The organisation’s settings screen.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required'),
        404: problemResponse('No such organisation, or not one you can see.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const putOrganizationSettingsRoute = createRoute({
    method: 'put',
    path: '/v1/organizations/{organizationId}/settings',
    operationId: 'putOrganizationSettings',
    tags: ['settings'],
    summary: 'Change the organisation’s settings',
    description: 'Sets or resets values at organisation scope, all or none. Requires `settings:read` and, for each change, the setting’s `editPermission`, ' +
        'and a member whose staff access covers every venue. Send the `ETag` you read as `If-Match`. Each change is audited.',
    [PERMISSION_EXTENSION]: SETTINGS_READ,
    [SETTING_PERMISSION_EXTENSION]: SETTING_PERMISSION_PER_KEY,
    request: {
        params: OrganizationScopeParamsSchema,
        headers: ConditionalWriteHeaderSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: PutSettingsSchema } }, required: true },
    },
    responses: {
        200: settingsResponse('The organisation’s settings screen as it is now.'),
        400: invalid,
        401: commonProblemResponses[401],
        402: entitlementRequired,
        403: forbidden,
        404: problemResponse('No such organisation, or not one you can see.', 'not-found'),
        412: stale,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getVenueSettingsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/settings',
    operationId: 'getVenueSettings',
    tags: ['settings'],
    summary: 'Read a venue’s settings screen',
    description: 'Every setting the venue’s screen shows, with its value, where it comes from, what it would inherit, the values stored for its station classes and zones, whether it is locked and whether you may change it. Requires `settings:read`.',
    [PERMISSION_EXTENSION]: SETTINGS_READ,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: settingsResponse('The venue’s settings screen.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required'),
        404: problemResponse('No such venue in this organisation, or not one you can see.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const putVenueSettingsRoute = createRoute({
    method: 'put',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/settings',
    operationId: 'putVenueSettings',
    tags: ['settings'],
    summary: 'Change a venue’s settings',
    description: 'Sets or resets values for the venue, or for one of its station classes or zones, all or none. Requires `settings:read` and, for each change, ' +
        'the setting’s `editPermission`. Refused with `setting-locked` when HQ has locked the setting above the venue. ' +
        'Send the `ETag` you read as `If-Match`. Each change is audited.',
    [PERMISSION_EXTENSION]: SETTINGS_READ,
    [SETTING_PERMISSION_EXTENSION]: SETTING_PERMISSION_PER_KEY,
    request: {
        params: VenueScopeParamsSchema,
        headers: ConditionalWriteHeaderSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: PutVenueSettingsSchema } }, required: true },
    },
    responses: {
        200: settingsResponse('The venue’s settings screen as it is now.'),
        400: invalid,
        401: commonProblemResponses[401],
        402: entitlementRequired,
        403: forbidden,
        404: problemResponse('No such venue in this organisation, or not one you can see.', 'not-found'),
        409: problemResponse('The venue is archived.', 'conflict'),
        412: stale,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=settings.js.map